import { AlertTriangle, Sparkles } from "lucide-react";
import { fmtDateTime } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { AssetPicker, EDITABLE, RewritePanel } from "@/components/DraftTools";
import { StatusBadge } from "@/components/common";
import { AssetThumb } from "@/components/AssetThumb";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ManualPublishPanel from "@/components/ManualPublish";

function LinkedAssets({ ids }) {
  const q = useWsQuery("/r/brand_assets", { enabled: ids.length > 0 });
  const assets = (q.data || []).filter((a) => ids.includes(a.id));
  if (!ids.length) return <p className="text-xs text-[#7C8BAD]">No approved assets were a fit for this piece.</p>;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="content-preview-assets">
      {assets.map((a) => <div key={a.id} className="rounded-lg border border-[#172342] overflow-hidden"><AssetThumb asset={a} className="aspect-[4/3]" /><div className="p-1.5 text-[11px] text-[#AAB6CC] truncate">{a.name}</div></div>)}
    </div>
  );
}

export default function ContentPreview({ item, onClose, onShowVersions }) {
  const { can } = useWorkspace();
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="content-preview-dialog" className="bg-[#0A1024] border-[#243153] text-[#F7FAFF] max-w-3xl max-h-[88vh] overflow-y-auto">
        {item && (<>
          <DialogHeader><DialogTitle className="font-display pr-6">{item.title}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#7C8BAD]">
            <StatusBadge status={item.status} />{item.source === "ai" && <span className="cq-chip text-[#B89CFF] border-[#855BFF]/40 bg-[#855BFF]/10"><Sparkles size={11} /> AI draft</span>}
            <span>{item.channel} · {item.format} · {item.campaign_name || "No campaign"} · v{item.version || 1} · {fmtDateTime(item.updated_at)}</span>
          </div>
          {item.compliance_flags?.length > 0 && (
            <div data-testid="content-preview-flags" className="p-3 rounded-lg border border-[#FF7792]/30 bg-[#FF7792]/5 text-xs text-[#FFB3C1] space-y-1">
              {item.compliance_flags.map((f) => <div key={f} className="flex gap-2"><AlertTriangle size={13} className="shrink-0" /> {f}</div>)}
            </div>)}
          <div className="p-4 rounded-lg bg-[#050816] border border-[#172342] text-sm text-[#D5DDEB] whitespace-pre-wrap leading-relaxed" data-testid="content-preview-body">{item.body || "No body yet."}</div>
          {item.rationale && <div><div className="cq-muted-label mb-1">Why this draft</div><p className="text-sm text-[#AAB6CC]">{item.rationale}</p></div>}
          {item.claims_needing_evidence?.length > 0 && (
            <div data-testid="content-preview-claims"><div className="cq-muted-label mb-1">Claims needing evidence before approval</div>
              <ul className="text-sm text-[#F4B860] list-disc pl-5 space-y-0.5">{item.claims_needing_evidence.map((c) => <li key={c}>{c}</li>)}</ul></div>)}
          {item.source === "ai" && !EDITABLE.includes(item.status) && <div><div className="cq-muted-label mb-2">Approved assets referenced</div><LinkedAssets ids={item.asset_ids || []} /></div>}
          {EDITABLE.includes(item.status) && can("edit") && (<>
            <AssetPicker item={item} />
            <RewritePanel item={item} onShowVersions={onShowVersions} />
          </>)}
          {["approved", "published_manual"].includes(item.status) && <ManualPublishPanel item={item} />}
          {!EDITABLE.includes(item.status) && <p data-testid="content-preview-locked" className="text-xs text-[#7C8BAD]">Rewrites and asset changes are locked while this is in review, approved or published.</p>}
          <p className="text-xs text-[#7C8BAD]">CRESQEN never publishes automatically. Content moves to Approved only through the Approval Queue, and is marked published only when a person records it.</p>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
